/*  ========================================================================
    # Components - Post Card
    ========================================================================  */

import React from 'react';
import tailwindConfig from '../../../tailwind.config';
import type { Post } from '@/react/types/post';
import bem from '../utils/bem';

// Hooks
import useLocalization from '@/react/hooks/useLocalization';
import useTranslation from '@/react/hooks/useTranslation';

// Components
import LazyImage from '@/react/components/LazyImage';
import LazySource from '@/react/components/LazySource';

export const PostCardSkeleton = () => {
  return (
    <div className='c-post-card c-post-card--skeleton'>
      <div className='c-post-card__placeholder-image'>
        <div className='c-post-card__placeholder-category' />
      </div>
      <div className='c-post-card__content'>
        <div className='c-post-card__placeholder-title' />
        <div className='c-post-card__placeholder-date' />
        <div className='c-post-card__placeholder-excerpt' />
      </div>
    </div>
  );
};

export const PostCard = ({ permalink, category, date, title, excerpt, image }: Post) => {
  const localize = useLocalization();
  const __ = useTranslation();

  const formatDate = (date: string) => {
    return Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' }).format(new Date(date));
  };

  return (
    <a
      className={bem('c-post-card', '', { 'no-image': Object.keys(image.sizes).length === 0 })}
      href={permalink}
    >
      <div className='c-post-card__image'>
        {image && Object.keys(image.sizes).length > 0 ? (
          <LazyImage
            defaultUrl={image.sizes.large}
            placeholderUrl={image.sizes.placeholder}
            alt={image.alt}
          >
            <LazySource srcSet={image.sizes.large} mediaMin={tailwindConfig.theme.screens.sm} />
          </LazyImage>
        ) : null}
        <span className='c-post-card__category'>{category}</span>
      </div>

      <div className='c-post-card__content'>
        <h3 className='c-post-card__title h4' dangerouslySetInnerHTML={{ __html: title }} />
        <div className='c-post-card__date'>
          <svg viewBox='0 0 24 24'>
            <use href={`${localize('svgSprite')}#calendar`} />
          </svg>
          <span>{formatDate(date)}</span>
        </div>
        <div className='c-post-card__excerpt' dangerouslySetInnerHTML={{ __html: excerpt }} />
        <div className='c-post-card__link'>
          <span>{__('component-post-card-link')}</span>
          <svg viewBox='0 0 24 24'>
            <use href={`${localize('svgSprite')}#arrow-right`} />
          </svg>
        </div>
      </div>
    </a>
  );
};
